import { useState } from "react";
import { Phone, Mail, Sparkles, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { getLeadTracking, trackLeadDeduped } from "../lib/fbpixel";
import { trackGaLead } from "../lib/ga";

/**
 * Shown when an AI generation is unavailable for the user:
 *   - "limit_reached" — they've used all 3 lifetime generations
 *   - "capped"        — the site has hit its daily budget cap
 *
 * Always provides 3 ways forward: call, email, or request a personal design.
 */
export default function LimitReachedPanel({ variant, message, lead, form }) {
  const isLifetimeLimit = variant === "limit_reached";
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const requestDesigner = async () => {
    if (sending || done) return;
    setSending(true);
    try {
      const tracking = getLeadTracking();
      await api.post("/leads", {
        ...lead,
        lead_type: "yard_designer",
        source_page: "yard-designer-limit",
        message: `[${isLifetimeLimit ? "Lifetime limit reached" : "Daily capacity reached"}] User requesting a personally crafted yard design.`,
        metadata: { variant, form },
        ...tracking,
      });
      trackLeadDeduped(tracking.event_id, { content_category: "yard_designer", source_page: "yard-designer-limit" });
      trackGaLead({ lead_type: "yard_designer", source_page: "yard-designer-limit" });
      toast.success("Request sent. A senior designer will be in touch shortly.");
      setDone(true);
    } catch {
      toast.error("Could not send. Please call or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="limit-reached-panel"
      className="border-2 border-[#8C1D30] bg-gradient-to-br from-[#8C1D30]/15 via-[#151515] to-[#151515] p-6 md:p-10 mb-10"
    >
      <div className="overline text-[#8C1D30] mb-3 flex items-center gap-2">
        <Sparkles className="w-4 h-4" />
        {isLifetimeLimit ? "AI Limit Reached" : "Daily Capacity Reached"}
      </div>

      <h2 className="font-serif-display text-3xl md:text-4xl leading-tight mb-4">
        {isLifetimeLimit
          ? "You've used all your AI generations — but we'd love to design your yard personally."
          : "Our AI is at capacity today — but our designers aren't."}
      </h2>

      <p className="text-[#D8D6D0]/80 leading-relaxed max-w-2xl">
        {message ||
          (isLifetimeLimit
            ? "Each email is limited to 3 AI generations. For further variations, our senior designers at NorthCrest will craft custom concepts based on your specific yard, style and budget — at no cost."
            : "We'll personally craft your yard concepts and reach out within 1 hour during business hours.")}
      </p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
        <a
          href="tel:+17806557604"
          data-testid="limit-call-btn"
          className="group flex items-center justify-center gap-3 px-6 py-5 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] transition-colors duration-300"
        >
          <Phone className="w-5 h-5" />
          <div className="text-left">
            <div className="overline text-[#F5F3EF]/80">Call now</div>
            <div className="text-sm font-medium">(780) 655-7604</div>
          </div>
        </a>

        <a
          href="mailto:northcrestedmonton@gmail.com?subject=Yard Design Request"
          data-testid="limit-email-btn"
          className="group flex items-center justify-center gap-3 px-6 py-5 border border-[#F5F3EF]/40 hover:bg-[#F5F3EF] hover:text-[#151515] text-[#F5F3EF] transition-all duration-300"
        >
          <Mail className="w-5 h-5" />
          <div className="text-left">
            <div className="overline text-[#D8D6D0]/70 group-hover:text-[#151515]/70">Email us</div>
            <div className="text-sm font-medium">northcrestedmonton@gmail.com</div>
          </div>
        </a>

        <button
          onClick={requestDesigner}
          disabled={sending || done}
          data-testid="limit-designer-btn"
          className="flex items-center justify-center gap-3 px-6 py-5 border border-[#D8D6D0]/30 hover:border-[#F5F3EF] text-[#F5F3EF] transition-colors duration-300 disabled:opacity-50"
        >
          {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
          <div className="text-left">
            <div className="overline text-[#D8D6D0]/70">Personal design</div>
            <div className="text-sm font-medium">
              {done ? "Request sent ✓" : "Have a designer call me"}
            </div>
          </div>
        </button>
      </div>

      <p className="text-xs text-[#D8D6D0]/40 mt-6">
        Business hours: Mon–Sat 8am–6pm MT · Edmonton, Alberta and surrounding areas.
      </p>
    </div>
  );
}
